import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import {
  Sidebar as SidebarContainer,
  SidebarContent,
  SidebarHeader,
  SidebarNav,
  SidebarNavHeader,
} from "../../ui/sidebar";
import { useHotel } from "@/hooks/useHotel";
import { useTenantProfile } from "@/hooks/useTenantProfile";
import { useQuery } from "@tanstack/react-query";
import { hotelService } from "@/services/hotel-service";
import { useAccessCapabilities } from "@/hooks/useAccessCapabilities";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { collectNavHrefs } from "@/components/layout/utils/isNavLinkActive";
import { SidebarNavItemRow } from "@/components/layout/components/SidebarNavItems";
import {
  type NavItem,
  type NavGroup,
  isHotelDefaultModePath,
  isHotelSettingsModePath,
} from "@/components/layout/constants/navigation";
import {
  isNavigationHrefVisible,
  useGetNavigationMenu,
} from "@/components/layout/hooks/useGetNavigationMenu";
import { PresignedImageWithPlaceholder } from "@/components/common/PresignedImageWithPlaceholder";
import { TenantLogoImageWithPlaceholder } from "@/components/common/TenantLogoImageWithPlaceholder";
import logo from "@/assets/svg/logo.svg";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuGroup,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import {
  CalendarCheck,
  ChevronDown,
  ChevronUp,
  ChevronsUpDown,
  DoorOpen,
  Settings,
  Users,
} from "lucide-react";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import { useLayoutRole } from "@/hooks/useLayoutRole";
import { ProfileHotelAccess } from "@/types/hotel";
import { isTenantAdminRole } from "@/utils/accessControl";
import { setSelectedHotelId, useSelectedHotelId } from "@/utils/hotelContext";

interface HotelBrandSelectorProps {
  hotelBrand: string;
  hotelLogoId: string | undefined;
  canSwitchHotels: boolean;
  otherHotels: ProfileHotelAccess[];
  onHotelSelect: (hotelId: string) => void;
}

const HotelBrandSelector: React.FC<HotelBrandSelectorProps> = ({
  hotelBrand,
  hotelLogoId,
  canSwitchHotels,
  otherHotels,
  onHotelSelect,
}) => {
  const { t } = useTranslation();

  const logoPlaceholder = (
    <Avatar>
      <AvatarFallback>
        {hotelBrand.trim()
          ? hotelBrand
              .split(" ")
              .slice(0, 2)
              .map((word) => word.charAt(0))
              .join("")
              .toUpperCase()
          : "??"}
      </AvatarFallback>
    </Avatar>
  );

  const hotelInfo = (
    <>
      <PresignedImageWithPlaceholder
        className="h-10 w-10 shrink-0 rounded-md object-cover"
        photo={hotelLogoId}
        placeholder={logoPlaceholder}
        alt={hotelBrand || t("layout.hotelLogoAlt")}
      />
      <span className="line-clamp-2 min-w-0 break-words" title={hotelBrand}>
        {hotelBrand}
      </span>
    </>
  );

  if (!canSwitchHotels) {
    return (
      <div className="border-base-border flex w-full items-center gap-2 rounded-md border px-4 py-2 text-sm text-black">
        {hotelInfo}
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="new_outline" className="h-auto w-full">
          <span className="flex w-full min-w-0 items-center justify-between gap-2">
            <div className="flex min-w-0 items-center gap-2">{hotelInfo}</div>
            <ChevronsUpDown className="size-4 shrink-0 text-zinc-400" />
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-[var(--radix-dropdown-menu-trigger-width)]">
        {otherHotels.map((h) => (
          <DropdownMenuItem
            key={h.hotelId}
            disabled={!isProfileHotelEnabled(h)}
            onSelect={() => onHotelSelect(h.hotelId)}
          >
            <span title={h.hotelName}>{h.hotelName}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

interface SidebarProps {
  open: boolean;
}

type SidebarMode = "default" | "settings";

const isProfileHotelEnabled = (hotel: ProfileHotelAccess): boolean =>
  ((hotel as Partial<Record<"isEnabled" | "isActive", boolean>>).isEnabled ??
    (hotel as Partial<Record<"isEnabled" | "isActive", boolean>>).isActive) !==
  false;

const HOTEL_SETTINGS_FALLBACK_HREF = "/custom-pages";
const HOTEL_SETTINGS_PRIMARY_HREFS = new Set<string>([
  "/general-info",
  "/reservations/rooms",
  "/amenities",
  "/pms-integration",
  "/notifications-settings",
  "/mobile-settings",
]);
const HOTEL_SETTINGS_ENTRY_HREF_PRIORITY = [
  "/general-info",
  "/mobile-settings",
  HOTEL_SETTINGS_FALLBACK_HREF,
  "/reservations/rooms",
  "/amenities",
  "/pms-integration",
  "/notifications-settings",
  "/checkin-settings",
  "/messaging-settings",
  "/checkout-settings",
  "/feedback-settings",
  "/guesteria-partners",
  "/billing-settings",
  "/billing-transactions",
];

const collectNavItemHrefs = (items: NavItem[]): string[] =>
  items.flatMap((item) => [
    item.href,
    ...(item.items ? collectNavItemHrefs(item.items) : []),
  ]);

const findSettingsEntryHref = (groups: NavGroup[]): string | null => {
  const visibleHrefs = groups.flatMap((group) =>
    collectNavItemHrefs(group.items),
  );

  return (
    HOTEL_SETTINGS_ENTRY_HREF_PRIORITY.find((href) =>
      visibleHrefs.includes(href),
    ) ??
    visibleHrefs.find(
      (href) =>
        href !== "/dashboard" &&
        href !== "/general-info" &&
        !HOTEL_SETTINGS_PRIMARY_HREFS.has(href),
    ) ??
    (visibleHrefs.includes("/general-info") ? "/general-info" : null)
  );
};

export const Sidebar: React.FC<SidebarProps> = ({ open }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { userProfile } = useAuthenticatedUser();
  const layoutRole = useLayoutRole();

  const isHotelScopedRole = layoutRole === "HOTEL_SCOPED";
  const supportsHotelSettingsNavigation = layoutRole === "HOTEL_SCOPED";

  const { capabilities } = useAccessCapabilities();

  const brandName = t("layout.brand");
  const { hotel } = useHotel(isHotelScopedRole);
  const { data: profileHotels = [] } = useQuery({
    queryKey: ["profileHotels"],
    queryFn: () =>
      hotelService.getProfileHotels({
        includeHotelDetailsFallback: false,
      }),
    enabled: isHotelScopedRole,
  });
  const activeProfileHotels = useMemo(
    () => profileHotels.filter(isProfileHotelEnabled),
    [profileHotels],
  );

  const selectedHotelIdFromStorage = useSelectedHotelId();
  const selectedHotelId =
    selectedHotelIdFromStorage ??
    hotel?.id ??
    activeProfileHotels[0]?.hotelId ??
    userProfile?.profile?.hotelId;
  const switchableProfileHotels = useMemo(
    () =>
      selectedHotelId
        ? activeProfileHotels.filter((h) => h.hotelId !== selectedHotelId)
        : activeProfileHotels,
    [activeProfileHotels, selectedHotelId],
  );
  const currentProfileHotel = useMemo(
    () =>
      activeProfileHotels.find(
        (profileHotel) => profileHotel.hotelId === selectedHotelId,
      ),
    [activeProfileHotels, selectedHotelId],
  );

  const canReadTenantProfile = isTenantAdminRole(userProfile);
  const { data: tenantProfile } = useTenantProfile(canReadTenantProfile);
  const tenantHasLogo = Boolean(tenantProfile?.hasLogo);
  const hotelBrand = currentProfileHotel?.hotelName ?? hotel?.name;
  const hotelLogoId = selectedHotelId === hotel?.id ? hotel?.logoId : undefined;

  const { defaultNavigation, settingsNavigation } = useGetNavigationMenu();
  const [brandMenuOpen, setBrandMenuOpen] = useState(false);
  const [sidebarMode, setSidebarMode] = useState<SidebarMode>(() => {
    return supportsHotelSettingsNavigation &&
      isHotelSettingsModePath(location.pathname)
      ? "settings"
      : "default";
  });

  useEffect(() => {
    if (!isHotelScopedRole || activeProfileHotels.length === 0) {
      return;
    }

    const hasSelectedActiveHotel =
      selectedHotelId &&
      activeProfileHotels.some(
        (profileHotel) => profileHotel.hotelId === selectedHotelId,
      );

    if (hasSelectedActiveHotel) {
      return;
    }

    setSelectedHotelId(activeProfileHotels[0].hotelId);
  }, [activeProfileHotels, isHotelScopedRole, selectedHotelId]);

  useEffect(() => {
    if (!supportsHotelSettingsNavigation) {
      return;
    }

    if (isHotelSettingsModePath(location.pathname)) {
      setSidebarMode("settings");
      return;
    }

    if (isHotelDefaultModePath(location.pathname)) {
      setSidebarMode("default");
    }
  }, [location.pathname, supportsHotelSettingsNavigation]);

  const navigation =
    supportsHotelSettingsNavigation && sidebarMode === "settings"
      ? settingsNavigation
      : defaultNavigation;
  const allNavHrefs = useMemo(
    () => collectNavHrefs(navigation.flatMap((group) => group.items)),
    [navigation],
  );
  const settingsEntryHref = useMemo(
    () =>
      supportsHotelSettingsNavigation
        ? findSettingsEntryHref(settingsNavigation)
        : null,
    [settingsNavigation, supportsHotelSettingsNavigation],
  );

  const navigationVisibilityContext = useMemo(
    () => ({
      capabilities,
      isHotelScopedRole,
      role: userProfile?.role,
      userProfile,
    }),
    [capabilities, isHotelScopedRole, userProfile],
  );

  const brandMenuItems = useMemo(() => {
    if (!supportsHotelSettingsNavigation) {
      return [];
    }

    return [
      {
        href: "/reservations",
        icon: <CalendarCheck className="h-8 w-8 text-black/55" />,
        label: t("layout.brandMenu.reservations"),
        mode: "default" as const,
      },
      {
        href: "/reservations/guests",
        icon: <Users size="2rem" className="h-8 w-8 text-black/55" />,
        label: t("layout.brandMenu.guests"),
        mode: "default" as const,
      },
      {
        href: "/reservations/rooms",
        icon: <DoorOpen size="2rem" className="h-8 w-8 text-black/55" />,
        label: t("layout.brandMenu.rooms"),
        mode: "settings" as const,
      },
      ...(settingsEntryHref
        ? [
            {
              href: settingsEntryHref,
              icon: <Settings size="2rem" className="h-8 w-8 text-black/55" />,
              label: t("layout.brandMenu.hotelSettings"),
              mode: "settings" as const,
            },
          ]
        : []),
    ].filter((item) =>
      isNavigationHrefVisible(item.href, navigationVisibilityContext),
    );
  }, [
    navigationVisibilityContext,
    settingsEntryHref,
    supportsHotelSettingsNavigation,
    t,
  ]);
  const canOpenBrandMenu = brandMenuItems.length > 0;
  const canSwitchHotels = switchableProfileHotels.length > 0;

  const handleBrandMenuNavigate = (href: string, mode: SidebarMode) => {
    if (supportsHotelSettingsNavigation) {
      setSidebarMode(mode);
    }

    navigate(href);
  };

  const handleHotelSelect = useCallback(
    (hotelId: string) => {
      const selectedHotel = profileHotels.find(
        (profileHotel) => profileHotel.hotelId === hotelId,
      );

      if (
        !hotelId ||
        hotelId === selectedHotelId ||
        !selectedHotel ||
        !isProfileHotelEnabled(selectedHotel)
      ) {
        return;
      }

      setSelectedHotelId(hotelId);
    },
    [profileHotels, selectedHotelId],
  );

  return (
    <motion.div
      animate={{
        width: open ? "12.5rem" : 0,
        opacity: open ? 1 : 0,
      }}
      transition={{
        duration: 0.3,
        ease: "easeInOut",
      }}
      className={cn("sticky top-0 h-[100dvh] shrink-0 overflow-hidden", {
        "pointer-events-none": !open,
      })}
    >
      <SidebarContainer
        className={cn(
          "font-geist bg-secondary-badge flex h-full flex-col overflow-hidden px-2 py-2",
        )}
      >
        <SidebarHeader
          className={"mb-6 flex h-auto shrink-0 flex-col gap-6 border-none p-0"}
        >
          <DropdownMenu
            open={canOpenBrandMenu ? brandMenuOpen : false}
            onOpenChange={canOpenBrandMenu ? setBrandMenuOpen : undefined}
          >
            <DropdownMenuTrigger asChild disabled={!canOpenBrandMenu}>
              <Button
                variant="new_ghost"
                className="data-[state=open]:border-custom-outline focus-visible:border-base-border flex h-auto w-full items-end justify-between border-1 border-transparent bg-transparent py-2 hover:bg-white focus-visible:border-1 focus-visible:bg-white disabled:pointer-events-none disabled:opacity-100 data-[state=open]:border-1 data-[state=open]:bg-white"
              >
                <span className="flex min-w-0 items-center gap-2">
                  {tenantHasLogo ? (
                    <TenantLogoImageWithPlaceholder
                      logoKey="tenant-logo"
                      className="h-6 w-auto max-w-[6rem] shrink-0 object-contain"
                      placeholder={
                        <img
                          src={logo}
                          alt={tenantProfile?.name ?? brandName}
                          className="h-6 w-auto shrink-0"
                        />
                      }
                      alt={tenantProfile?.name ?? brandName}
                    />
                  ) : (
                    <img
                      src={logo}
                      alt={brandName}
                      className="h-6 w-auto shrink-0"
                    />
                  )}
                  <span className="text-foreground truncate text-sm font-medium">
                    {tenantProfile?.name ?? brandName}
                  </span>
                </span>
                {canOpenBrandMenu &&
                  (brandMenuOpen ? (
                    <ChevronUp className="ml-auto size-4 shrink-0 text-zinc-400" />
                  ) : (
                    <ChevronDown className="ml-auto size-4 shrink-0 text-zinc-400" />
                  ))}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-[var(--radix-dropdown-menu-trigger-width)]">
              <DropdownMenuGroup>
                {brandMenuItems.map((item) => (
                  <DropdownMenuItem
                    key={item.href}
                    className="text-md/[20px]"
                    onSelect={() =>
                      handleBrandMenuNavigate(item.href, item.mode)
                    }
                  >
                    {item.icon}
                    {item.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuGroup>
            </DropdownMenuContent>
          </DropdownMenu>

          {hotelBrand && (
            <HotelBrandSelector
              hotelBrand={hotelBrand}
              hotelLogoId={hotelLogoId}
              canSwitchHotels={canSwitchHotels}
              otherHotels={switchableProfileHotels}
              onHotelSelect={(hotelId) => void handleHotelSelect(hotelId)}
            />
          )}
        </SidebarHeader>

        <SidebarContent className="flex flex-1 flex-col gap-1 overflow-y-auto p-0">
          {navigation.map((group, index) => (
            <div
              key={`${group.title ?? "nav"}-${index}`}
              className="flex flex-col"
            >
              {group.title && (
                <SidebarNavHeader
                  className={"p-2 text-xs/[16px] font-normal text-neutral-400"}
                >
                  {group.title}
                </SidebarNavHeader>
              )}
              <SidebarNav className={"flex flex-col gap-1"}>
                {group.items.map((item) => (
                  <SidebarNavItemRow
                    key={item.href}
                    item={item}
                    allHrefs={allNavHrefs}
                  />
                ))}
              </SidebarNav>
            </div>
          ))}
        </SidebarContent>
      </SidebarContainer>
    </motion.div>
  );
};
